import { memo, useCallback, useMemo, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { assetsClipUpdate } from '@goldcast/api/content';
import { getIsDuplicateRecording, isNotFullLengthClip } from '@goldcast/player-config/src/utils/clipUtils';
import TooltipV2 from '../TooltipV2';
import ClipPublishStatusBadge, { hasVisiblePublishStatus } from './ClipPublish/ClipPublishStatusBadge';
import ClipPublishButton from './ClipPublish/ClipPublishButton';
import ClipUpdateInProgress from './ClipUpdateInProgress/ClipUpdateInProgress';
import ClipTitlePopup from '../atoms/ClipTitlePopup';
import IconButton from '../atoms/Button/IconButton';
import Icon from '../atoms/Icon';
import CoEditSparkleIcon from '../atoms/CoEditSparkleIcon';
import FreeTrialUpgradeNag from '@/components/molecules/FreeTrial/FreeTrialUpgradeNag';
import ClipUndoRedo from '@/components/molecules/ClipUndoRedo';
import DownloadsPopup from '@/components/molecules/DownloadsPopup/DownloadsPopup';
import ClipSharePopup from '@/components/molecules/SharePopup/ClipSharePopup/ClipSharePopup';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { useClipPendingUpdate } from '@/hooks/useClipPendingUpdate';
import { ClipSyncStatus } from '@/stores/pendingEditsStore';
import useSavedSearch from '@/hooks/useSavedSearch';
import useSmallScreen from '@/hooks/useSmallScreen';
import useFreeTrialHook from '@/hooks/useFreeTrialHook';
import ContentSettings from '@/components/molecules/ContentSettings';
import SavedSearchTitlePopup from '@/Pages/SavedSearch/SavedSearchTitlePopup';
import SessionTitlePopup from '@/Pages/SessionDetail/SessionTitlePopup';
import SessionDetailBackButton from '@/Pages/SessionDetail/SessionDetailBackButton';
import useAIEditsAvailability from '@/Pages/Clip/ClipPlayer/ClipCustomizer/useAIEditsAvailability';
import NewTemplateButton from '@/Pages/Clip/SideBar/TemplatesSideBar/NewTemplateButton';
import RecordingDuplicateDropdown from '@/Pages/RecordingsPage/RecordingDuplicateDropdown';
import { core } from '@/stores/core';
import { useAppContext } from '@/context/AppContext/AppContext';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { handleClipLockedError } from '@/Pages/Clip/utils';
import { classnames } from '@/libs/utils';
import { aiEditsStore } from '@/stores/aiEditsStore';
import { orgUsageSummaryState } from '@/stores/orgUsageSummary';
import { isClipEditorPath } from '@/utils/clipEditorRoutes';

function ClipHeader({ showLock, newPlayer }: { showLock?: boolean; newPlayer?: boolean }) {
  const coreStore = useSyncExternalStore(core.subscribe, core.getSnapshot);
  const { adminAppStore } = useAppContext();
  const { clipData } = useClipsContext();

  const location = useLocation();
  const { isSavedSearchPage } = useSavedSearch();

  // Responsive breakpoint detection
  const isSmallScreen = useSmallScreen();

  const { isAIEditsSidebarOpen } = useSyncExternalStore(aiEditsStore.subscribe, aiEditsStore.getSnapshot);
  const { isAIEditsHidden, isAiEditsDisabled, AIEditsDisabledReason } = useAIEditsAvailability();

  const handleCoEditClick = useCallback(() => {
    if (isAIEditsSidebarOpen) {
      window.analytics?.track('ContentLabEvent', {
        name: 'AIEditsSidebarClosed',
        eventTrigger: 'ManualClick',
        clipId: clipData.id,
        mediaType: coreStore.content?.media_type
      });
      aiEditsStore.setIsAIEditsSidebarOpen(false);
      return;
    }
    window.analytics?.track('ContentLabEvent', {
      name: 'AIEditsSidebarOpened',
      eventTrigger: 'ManualClick',
      clipId: clipData.id,
      mediaType: coreStore.content?.media_type
    });
    aiEditsStore.setIsAIEditsSidebarOpen(true, 'ManualClick');
  }, [isAIEditsSidebarOpen, clipData.id, coreStore.content?.media_type]);

  const orgUsageSummaryStore = useSyncExternalStore(orgUsageSummaryState.subscribe, orgUsageSummaryState.getSnapshot);
  const { isFreeTrialEnabled, isFreemiumUser } = useFreeTrialHook();
  const isFreeTrialButtonEnabled = useMemo(() => {
    const showUpgradeButton = isFreeTrialEnabled || isFreemiumUser;
    const isStoreReady = !orgUsageSummaryStore.loading && !orgUsageSummaryStore.error && orgUsageSummaryStore.orgId;
    return showUpgradeButton && isStoreReady;
  }, [
    isFreeTrialEnabled,
    isFreemiumUser,
    orgUsageSummaryStore.loading,
    orgUsageSummaryStore.error,
    orgUsageSummaryStore.orgId
  ]);

  const isRecordingsPage = useMemo(() => {
    return location.pathname.includes('/recordings');
  }, [location.pathname]);

  const isClipListPage = useMemo(() => {
    return isClipEditorPath(location.pathname);
  }, [location.pathname]);

  const isVideoLibraryClipPublishEnabled = useMemo(
    () => adminAppStore.getters?.['org/getCurrentOrg']?.showVideoLibraryClipPublish || false,
    [adminAppStore]
  );

  const updateRecordingTitle = useCallback(() => {
    const title = core.getSnapshot().content?.title;
    if (isRecordingsPage && title) {
      assetsClipUpdate({
        id: clipData.id,
        body: {
          ...clipData,
          title,
          asset_metadata: clipData.asset_metadata
        }
      });
    }
  }, [clipData, isRecordingsPage]);

  const isPublishingAllowed = useMemo(() => {
    // For clip page
    if (isVideoLibraryClipPublishEnabled && isClipListPage) {
      return true;
    }

    // For recording studio recordings
    if (coreStore.content?.event_type === 'Recording' && isRecordingsPage) {
      return true;
    }

    // For event recordings
    if (coreStore.content?.media_source_type === 'RECORDING' && isRecordingsPage) {
      return true;
    }

    // For content lab uploads
    if (featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Uploads_In_VL] && isRecordingsPage) {
      return true;
    }

    return false;
  }, [
    coreStore.content?.event_type,
    coreStore.content?.media_source_type,
    isVideoLibraryClipPublishEnabled,
    isRecordingsPage,
    isClipListPage
  ]);

  // Nav status line shows the save state and the publish status mutually exclusively (never both):
  // while there are pending/unsaved changes the save state wins ("Saving…" / "Changes not saved");
  // once everything is saved, the publish status shows ("Publishing" / "Publishing Failed" /
  // "Last Published …"), falling back to "All changes saved" when there's nothing to publish.
  const { status: clipSyncStatus } = useClipPendingUpdate({ clipId: clipData.id });
  const showPublishStatus =
    isPublishingAllowed &&
    clipSyncStatus === ClipSyncStatus.sync_completed &&
    hasVisiblePublishStatus(clipData.asset_metadata);

  const shouldShowClipTitle = useMemo(() => {
    if (isNotFullLengthClip(clipData) || getIsDuplicateRecording(clipData)) {
      return true;
    }
    return false;
  }, [clipData]);

  const shouldShowTemplateAsIcon = useMemo(() => {
    return Boolean(isSmallScreen || isFreeTrialButtonEnabled);
  }, [isSmallScreen, isFreeTrialButtonEnabled]);

  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isDownloadRevampEnabled = !!featureFlags[FeatureFlagKeys.Use_CL_Download_Revamp];
  const isClipDuplicationEnabled = !!featureFlags[FeatureFlagKeys.Use_CL_Clip_Duplication];

  return (
    <div
      className={classnames(
        'relative flex items-center justify-between',
        newPlayer ? 'h-16 w-full border-b border-card-border pr-4' : 'p-4'
      )}
    >
      <div className="flex min-w-0 flex-1 items-center space-x-2">
        {newPlayer && (
          // Same width as the left rail (w-20) so the back button centers over the rail icons.
          <div className="flex w-20 shrink-0 items-center justify-center">
            <SessionDetailBackButton inNav />
          </div>
        )}
        <div className={classnames('flex min-w-0 flex-col justify-center', { 'pl-4': newPlayer })}>
          <div className="flex min-w-0 items-center space-x-1">
            {!newPlayer && <ClipUpdateInProgress clipId={clipData.id} />}
            <div
              className={classnames('flex min-w-0 items-center', {
                // When Co-edit is open the nav is narrower, so let the title truncate (ellipsis)
                // as space shrinks and only drop it entirely on very narrow viewports.
                'max-md:hidden': isAIEditsSidebarOpen
              })}
            >
              {isSavedSearchPage ? (
                <>
                  <SavedSearchTitlePopup />
                  {clipData?.saved_search?.search_term && (
                    <TooltipV2
                      tooltipId="edit-saved-search-title-tooltip"
                      tooltipElement={<Icon name="IconInfoCircle" size={16} className="ml-1 text-slate-950" />}
                      tooltipContent={clipData?.saved_search?.search_term}
                      size="xsmall"
                      colorTheme="dark"
                      place="right"
                      className="z-50 !rounded-md bg-slate-950 px-3 py-2 !text-[10px] font-normal !leading-[15px] text-white"
                    />
                  )}
                </>
              ) : (
                <>
                  {shouldShowClipTitle ? <ClipTitlePopup /> : <SessionTitlePopup onUpdate={updateRecordingTitle} />}
                  {isRecordingsPage && isClipDuplicationEnabled && <RecordingDuplicateDropdown />}
                </>
              )}
            </div>
            {!newPlayer && isPublishingAllowed && (
              <div className={classnames({ 'max-2xl:hidden': isAIEditsSidebarOpen })}>
                <ClipPublishStatusBadge />
              </div>
            )}
            {showLock && (
              <IconButton
                icon="IconLock"
                title={`Clip is published in ${clipData.content.project.title}'s on-demand page and cannot be edited. Please un-publish to continue editing.`}
                onClick={() => handleClipLockedError(clipData)}
                trackingId="clips-list-item-locked-button"
                variation="outline"
                iconClassName="h-4 w-4 text-slate-600"
                buttonClassName="h-8 w-8"
              />
            )}
          </div>
          {newPlayer && (
            // min-w-0 + overflow-hidden + whitespace-nowrap keep the status on a single line so it
            // never wraps below the nav; the label truncates with an ellipsis when space is tight.
            // The save icon (ClipUpdateInProgress) always shows on the left, matching develop's
            // tooltips. The status text beside it is save-or-publish, mutually exclusive (see
            // showPublishStatus): while there are unsaved changes the save label wins; once saved,
            // the publish status shows. showLabel already renders the icon + save pill together, so
            // the publish branch renders the icon separately alongside the publish badge.
            <div className="flex h-6 min-w-0 items-center gap-1.5 overflow-hidden whitespace-nowrap px-2 leading-tight">
              {showPublishStatus ? (
                <>
                  <ClipUpdateInProgress clipId={clipData.id} size={16} />
                  <ClipPublishStatusBadge />
                </>
              ) : (
                <ClipUpdateInProgress clipId={clipData.id} showLabel size={16} />
              )}
            </div>
          )}
        </div>
        {newPlayer && <ClipUndoRedo isInline={true} />}
      </div>
      <div className="flex shrink-0 items-center space-x-3">
        <FreeTrialUpgradeNag />
        {!newPlayer && <ClipUndoRedo isInline={true} />}
        {isDownloadRevampEnabled ? (
          <>
            <NewTemplateButton
              className={classnames('bg-white', shouldShowTemplateAsIcon ? 'w-[38px]' : '')}
              isIconOnly={shouldShowTemplateAsIcon}
            />
            <ClipSharePopup
              iconButtonVariation={isPublishingAllowed ? 'outline' : 'filled'}
              activeClipId={clipData.id}
              downloadIntent={location.state?.downloadIntent}
            />
            {coreStore.content?.id && <DownloadsPopup contentId={coreStore.content.id} />}
            <ClipPublishButton isPublishingAllowed={isPublishingAllowed} />
          </>
        ) : (
          <>
            {coreStore.content?.id && <DownloadsPopup contentId={coreStore.content.id} />}
            <NewTemplateButton
              className={classnames('bg-white', shouldShowTemplateAsIcon ? 'w-[38px]' : '')}
              isIconOnly={shouldShowTemplateAsIcon}
            />
            <ClipPublishButton isPublishingAllowed={isPublishingAllowed} />
            <ClipSharePopup
              iconButtonVariation={isPublishingAllowed ? 'outline' : 'filled'}
              activeClipId={clipData.id}
              downloadIntent={location.state?.downloadIntent}
            />
          </>
        )}
        {isSavedSearchPage && <ContentSettings />}
        {newPlayer && !isAIEditsHidden && (
          // Vertical divider separates the Co-edit action from the publish/share group (per Figma).
          <div className="h-6 w-px shrink-0 bg-slate-200" />
        )}
        {newPlayer && !isAIEditsHidden && (
          // title sits on the wrapper span so the disabled-reason tooltip still shows
          // (disabled buttons don't reliably surface native title tooltips).
          <span title={AIEditsDisabledReason}>
            <button
              type="button"
              onClick={handleCoEditClick}
              disabled={isAiEditsDisabled}
              data-testid="co-edit-button"
              className={classnames(
                'flex h-8 items-center gap-0.5 rounded-lg border px-2 text-sm font-medium text-black transition-colors',
                {
                  // Disabled — default look, whole button (incl. gradient sparkle) dimmed to 40%.
                  'co-edit-shadow cursor-not-allowed border-slate-100 bg-white opacity-40 shadow-sm': isAiEditsDisabled,
                  // Open (Co-edit active) — same treatment as the pressed state (subtle deep-orange
                  // border + pale deep-orange fill, no ring). The designer did not want a distinct
                  // outlined open state. deep-orange-50 over white keeps the fill light and stays on
                  // the themeable token via the opacity modifier.
                  'co-edit-shadow border-card-border-light bg-card-hover-bg shadow-sm':
                    !isAiEditsDisabled && isAIEditsSidebarOpen,
                  // Default → hover → pressed (pale deep-orange fill, not the darker -100).
                  'co-edit-interaction co-edit-shadow border-slate-100 bg-white shadow-sm hover:border-card-border active:border-card-border-light':
                    !isAiEditsDisabled && !isAIEditsSidebarOpen
                }
              )}
            >
              <CoEditSparkleIcon className="size-4" />
              <span className="px-1">Co-edit</span>
            </button>
          </span>
        )}
      </div>
    </div>
  );
}

export default memo(ClipHeader);
